import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { useAuth } from "../auth/AuthContext";
import { SearchBar } from "../components/SearchBar";
import { Pagination } from "../components/Pagination";

const LIMIT = 20;

function toDatetimeLocal(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocal(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return Number.NaN;
  return d.toISOString();
}

async function extractErrorMessage(response, fallback) {
  try {
    const text = await response.text();
    if (!text) return fallback;
    try {
      const parsed = JSON.parse(text);
      return parsed?.error || parsed?.message || fallback;
    } catch {
      if (text.startsWith("<!DOCTYPE")) return fallback;
      return text;
    }
  } catch {
    return fallback;
  }
}

function sessionClasses(value) {
  const session = String(value || "").toLowerCase();
  if (session === "on") return "bg-emerald-100 text-emerald-800";
  if (session === "off") return "bg-red-100 text-red-800";
  return "bg-slate-200 text-slate-700";
}

function emptyForm() {
  return {
    file_country: "",
    user_id: "",
    country: "",
    date_on: "",
    last_game: "",
    sesion: "",
    onpc: "",
    session_id: "",
    device_id: "",
    user_agent: "",
    ip: "",
    last_seen: "",
    user_type: "",
  };
}

function mapRowToForm(row) {
  return {
    file_country: row?.file_country || "",
    user_id: row?.user_id || "",
    country: row?.country || "",
    date_on: toDatetimeLocal(row?.date_on),
    last_game: row?.last_game || "",
    sesion: row?.sesion || "",
    onpc: row?.onpc ?? "",
    session_id: row?.session_id || "",
    device_id: row?.device_id || "",
    user_agent: row?.user_agent || "",
    ip: row?.ip || "",
    last_seen: toDatetimeLocal(row?.last_seen),
    user_type: row?.user_type ?? "",
  };
}

function normalizePayload(values) {
  const toNullableText = (v) => {
    const t = String(v ?? "").trim();
    return t ? t : null;
  };

  const toNullableInteger = (v) => {
    const t = String(v ?? "").trim();
    if (!t) return null;
    const num = Number(t);
    return Number.isInteger(num) ? num : Number.NaN;
  };

  const payload = {
    user_id: toNullableText(values.user_id),
    country: toNullableText(values.country),
    date_on: fromDatetimeLocal(values.date_on),
    last_game: toNullableText(values.last_game),
    sesion: toNullableText(values.sesion),
    onpc: toNullableInteger(values.onpc),
    session_id: toNullableText(values.session_id),
    device_id: toNullableText(values.device_id),
    user_agent: toNullableText(values.user_agent),
    ip: toNullableText(values.ip),
    last_seen: fromDatetimeLocal(values.last_seen),
    user_type: toNullableInteger(values.user_type),
  };

  return payload;
}

export default function GameslogAdmin() {
  const { authFetch } = useAuth();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const [editKey, setEditKey] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm());
  const [saving, setSaving] = useState(null);

  const totalPages = Math.max(1, Math.ceil((Number(total) || 0) / LIMIT));

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    let alive = true;

    async function loadRows() {
      try {
        setLoading(true);
        setError(null);

        const params = new URLSearchParams({
          page: String(page),
          limit: String(LIMIT),
        });
        if (search) params.set("search", search);

        const response = await authFetch(`/api/auth/admin/gameslog?${params.toString()}`);
        if (!response.ok) {
          const msg = await extractErrorMessage(response, "Error loading gameslog");
          throw new Error(msg);
        }

        const data = await response.json();
        if (!alive) return;

        setRows(Array.isArray(data?.rows) ? data.rows : []);
        setTotal(Number(data?.total) || 0);
      } catch (err) {
        if (!alive) return;
        console.error("Load gameslog error:", err);
        setError(err?.message || "Error loading gameslog");
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadRows();

    return () => {
      alive = false;
    };
  }, [authFetch, page, search]);

  const onStartEdit = (row) => {
    setEditKey(row.file_country);
    setEditForm(mapRowToForm(row));
  };

  const onSaveEdit = async () => {
    const payload = normalizePayload(editForm);

    if (
      Number.isNaN(payload.date_on) ||
      Number.isNaN(payload.last_seen) ||
      Number.isNaN(payload.onpc) ||
      Number.isNaN(payload.user_type)
    ) {
      await Swal.fire({
        icon: "warning",
        title: "Validation",
        text: "Check datetime and integer fields before saving.",
      });
      return;
    }

    try {
      setSaving(editKey);

      const response = await authFetch(`/api/auth/admin/gameslog/${encodeURIComponent(editKey)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const msg = await extractErrorMessage(response, "Error updating row");
        throw new Error(msg);
      }

      const data = await response.json();
      setRows((prev) => prev.map((row) => (row.file_country === editKey ? data.row : row)));
      setEditKey(null);

      await Swal.fire({
        icon: "success",
        title: "Saved",
        text: "Gameslog row updated successfully",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error("Update gameslog row error:", err);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.message || "Error updating row",
      });
    } finally {
      setSaving(null);
    }
  };

  const onDelete = async (fileCountry) => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Delete row?",
      text: `file_country: ${fileCountry}`,
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#d33",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    try {
      setSaving(fileCountry);

      const response = await authFetch(`/api/auth/admin/gameslog/${encodeURIComponent(fileCountry)}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        const msg = await extractErrorMessage(response, "Error deleting row");
        throw new Error(msg);
      }

      setRows((prev) => prev.filter((row) => row.file_country !== fileCountry));
      setTotal((prev) => Math.max(0, prev - 1));

      await Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Gameslog row deleted",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error("Delete gameslog row error:", err);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.message || "Error deleting row",
      });
    } finally {
      setSaving(null);
    }
  };

  const summary = useMemo(() => `${total} rows`, [total]);

  return (
    <div className="w-full p-4 md:p-6 font-montserrat space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Games Log</h2>
        <p className="text-gray-600">Manage records from the gameslog table.</p>
      </div>

      <div className="grid grid-cols-1 gap-4">
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <p className="text-sm text-slate-600">
              <span className="font-semibold text-slate-900">{summary}</span>
            </p>
            <SearchBar
              value={searchInput}
              onChange={setSearchInput}
              placeholder="Search by file_country, country, user_id, session_id, IP..."
            />
          </div>
        </div>
      </div>

      <div className="min-h-0">
        <div className="bg-white rounded-lg shadow-sm h-full overflow-auto">
          {loading ? (
            <div className="w-full p-6 flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4" />
                <p className="text-gray-600">Loading gameslog...</p>
              </div>
            </div>
          ) : error ? (
            <div className="p-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <h3 className="text-red-800 font-semibold mb-2">Error</h3>
                <p className="text-red-700 mb-4">{error}</p>
                <button
                  onClick={() => {
                    setPage(1);
                    setSearch((s) => s);
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500">No gameslog records found.</div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-700">file_country</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-700">country</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-700">user_id</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-700">last_game</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-700">sesion</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-700">last_seen</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-700">actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row) => (
                  <tr key={row.file_country} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900">{row.file_country}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{row.country || "-"}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{row.user_id || "-"}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{row.last_game || "-"}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${sessionClasses(row.sesion)}`}>
                        {row.sesion || "-"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {row.last_seen ? new Date(row.last_seen).toLocaleString() : "-"}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex gap-2">
                        <button
                          onClick={() => onStartEdit(row)}
                          disabled={saving === row.file_country}
                          className="rounded bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => onDelete(row.file_country)}
                          disabled={saving === row.file_country}
                          className="rounded bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="flex justify-end">
        <Pagination page={page} totalPages={totalPages} setPage={setPage} />
      </div>

      {editKey && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-4xl rounded-2xl bg-white shadow-xl flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h3 className="font-semibold text-slate-900">Edit gameslog: {editKey}</h3>
              <button onClick={() => setEditKey(null)} className="text-slate-400 hover:text-slate-700 text-xl leading-none">×</button>
            </div>

            <div className="overflow-y-auto px-6 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">country</span>
                  <input className="w-full rounded border border-slate-300 px-3 py-2 text-sm" value={editForm.country} onChange={(e) => setEditForm((p) => ({ ...p, country: e.target.value }))} placeholder="country" />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">user_id</span>
                  <input className="w-full rounded border border-slate-300 px-3 py-2 text-sm" value={editForm.user_id} onChange={(e) => setEditForm((p) => ({ ...p, user_id: e.target.value }))} placeholder="user_id (uuid)" />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">session_id</span>
                  <input className="w-full rounded border border-slate-300 px-3 py-2 text-sm" value={editForm.session_id} onChange={(e) => setEditForm((p) => ({ ...p, session_id: e.target.value }))} placeholder="session_id (uuid)" />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">device_id</span>
                  <input className="w-full rounded border border-slate-300 px-3 py-2 text-sm" value={editForm.device_id} onChange={(e) => setEditForm((p) => ({ ...p, device_id: e.target.value }))} placeholder="device_id" />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">ip</span>
                  <input className="w-full rounded border border-slate-300 px-3 py-2 text-sm" value={editForm.ip} onChange={(e) => setEditForm((p) => ({ ...p, ip: e.target.value }))} placeholder="ip" />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">last_game</span>
                  <input className="w-full rounded border border-slate-300 px-3 py-2 text-sm" value={editForm.last_game} onChange={(e) => setEditForm((p) => ({ ...p, last_game: e.target.value }))} placeholder="last_game" />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">sesion</span>
                  <input className="w-full rounded border border-slate-300 px-3 py-2 text-sm" value={editForm.sesion} onChange={(e) => setEditForm((p) => ({ ...p, sesion: e.target.value }))} placeholder="sesion" />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">onpc</span>
                  <input className="w-full rounded border border-slate-300 px-3 py-2 text-sm" value={editForm.onpc} onChange={(e) => setEditForm((p) => ({ ...p, onpc: e.target.value }))} placeholder="onpc" />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">user_type</span>
                  <input className="w-full rounded border border-slate-300 px-3 py-2 text-sm" value={editForm.user_type} onChange={(e) => setEditForm((p) => ({ ...p, user_type: e.target.value }))} placeholder="user_type" />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">date_on</span>
                  <input type="datetime-local" className="w-full rounded border border-slate-300 px-3 py-2 text-sm" value={editForm.date_on} onChange={(e) => setEditForm((p) => ({ ...p, date_on: e.target.value }))} />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">last_seen</span>
                  <input type="datetime-local" className="w-full rounded border border-slate-300 px-3 py-2 text-sm" value={editForm.last_seen} onChange={(e) => setEditForm((p) => ({ ...p, last_seen: e.target.value }))} />
                </label>
                <label className="block md:col-span-2">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">user_agent</span>
                  <input className="w-full rounded border border-slate-300 px-3 py-2 text-sm" value={editForm.user_agent} onChange={(e) => setEditForm((p) => ({ ...p, user_agent: e.target.value }))} placeholder="user_agent" />
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200">
              <button onClick={() => setEditKey(null)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">
                Cancel
              </button>
              <button
                onClick={onSaveEdit}
                disabled={saving === editKey}
                className="rounded-lg bg-[#03203d] px-4 py-2 text-sm font-medium text-white hover:bg-[#05407b] disabled:opacity-50"
              >
                {saving === editKey ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
